import { Stack, Typography } from '@mui/material';
import { CSSProperties, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import GoogleStoreIcon from 'infrastructure/targets/web/assets/icons/google-store.svg';

const timeout = 3_000; // in milliseconds
async function handleDeepLink(deeplink: string, storeUrl: string) {
  const startTime = Date.now();
  window.location.href = deeplink;

  const interval = setInterval(() => {
    if (Date.now() - startTime > timeout) {
      clearInterval(interval);
      window.location.href = storeUrl;
    }

    if (document.hidden) {
      clearInterval(interval);
    }
  }, 200);
}

// Store URLs for US build
const US_STORE_URLS: Record<'ios' | 'android', string> = {
  ios: 'https://apps.apple.com/us/app/five-iron-golf/id6470542123',
  android: 'https://play.google.com/store/apps/details?id=com.fiveirongolf.mobileapp&hl=en_US',
};

// Store URLs for International build (AU/UAE)
const INTL_STORE_URLS: Record<'ios' | 'android', string> = {
  ios: 'https://apps.apple.com/us/app/five-iron-golf-intl/id6746535709',
  android: 'https://play.google.com/store/apps/details?id=com.fiveirongolfintl.mobileapp&hl=en',
};

/**
 * Returns the appropriate store URLs based on the app build variant.
 * International builds (AU/UAE) use different app store listings than US builds.
 *
 * @param isInternational - Optional parameter to explicitly set international status (for mobile).
 *                          If not provided, will detect from domain (for web).
 */
export const getStoreURLs = (isInternational?: boolean): Record<'ios' | 'android', string> => {
  const isIntl =
    isInternational ??
    (typeof window !== 'undefined' &&
      (window?.location?.hostname?.includes('.ae') || window?.location?.hostname?.includes('.au')));

  return isIntl ? INTL_STORE_URLS : US_STORE_URLS;
};

/**
 * @deprecated Use getStoreURLs() instead to get dynamic URLs based on build variant
 */
export const StoreURL: Record<'ios' | 'android', string> = getStoreURLs();

const styles = {
  width: '246px',
  height: '82px',
  verticalAlign: 'middle',
  objectFit: 'contain',
} satisfies CSSProperties;

export const MobileDeeplink = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const deeplink = searchParams.get('deeplink');
    const platform = searchParams.get('platform') as 'ios' | 'android';
    const storeUrls = getStoreURLs();

    if (!deeplink || !platform || !storeUrls[platform]) {
      return;
    }

    handleDeepLink(deeplink, storeUrls[platform]);
  }, [searchParams]);

  return (
    <Stack
      gap={6}
      padding={4}
      alignItems="center"
      direction={'column'}
      sx={{ backgroundColor: 'black', height: '100dvh' }}>
      <Typography variant="h2" color="white" width={'100%'}>
        Five Iron Golf
      </Typography>

      <a href={getStoreURLs().ios} style={{ display: 'block' }}>
        <img
          src="https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/black/en-us?releaseDate=1702252800"
          alt="Download on the App Store"
          style={styles}
        />
      </a>

      <a href={getStoreURLs().android} style={{ display: 'block' }}>
        <img
          src={GoogleStoreIcon}
          alt="Get it on Google Play"
          style={{
            ...styles,
            border: '2px solid #a6a6a6',
            borderRadius: '12px',
          }}
        />
      </a>
    </Stack>
  );
};
